import { AppInsightsClient } from '@axa-asia/app-insights-client';
import { EmmaFactory } from '@axa-asia/hk-emma-js';
import _ from 'lodash';
import moment from 'moment';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import uuidv4 from 'uuid/v4';
import { goto } from '../../../actions/historyActions';
import { showErrorModal } from '../../../actions/uiActions';
import { putCustState } from '../../../actions/userActions';
import { ENTITY_CODE } from '../../../constants/constants';
import { store } from '../../../stores';
import { isApp } from '../../../utils';
import { passPath } from '../../../utils/emmaUrlConverter';
import { myAxaFetchWithToken } from '../../../utils/myAxaFetchWithToken';
import { DISPLAY_ERROR_CODE } from '../actions/changeContactAction';
import {
  FETCH_EMAIL_OTHER_POLICY_LIST,
  FETCH_EMAIL_OTHER_POLICY_LIST_FAIL,
  FETCH_EMAIL_OTHER_POLICY_LIST_SUCCESS,
  RESEND_EMAIL_OTP,
  RESEND_EMAIL_OTP_FAIL,
  RESEND_EMAIL_OTP_SUCCESS,
  REVIEW_CHANGE_EMAIL_FORM,
  SEND_EMAIL_OTP_REQUEST,
  SEND_EMAIL_OTP_REQUEST_FAIL,
  SEND_EMAIL_OTP_REQUEST_SUCCESS,
  SUBMIT_CHANGE_EMAIL_FORM,
  SUBMIT_CHANGE_EMAIL_FORM_FAIL,
  SUBMIT_CHANGE_EMAIL_FORM_SUCCESS,
  SUBMIT_CHANGE_EMAIL_REQUEST,
  SUBMIT_CHANGE_EMAIL_REQUEST_SUCCESS,
  UPDATE_EMAIL_CUST_STATE,
  UPDATE_EMAIL_CUST_STATE_FAILURE,
  UPDATE_EMAIL_CUST_STATE_SUCCESS,
  UPDATE_EMAIL_OTHER_POLICIES,
  updateEmailCustState as updateEmailCustStateAction,
  updateGoGreenList,
} from '../actions/changeEmailAction';
import {
  UPDATE_EMAIL_FEATURE_ID,
  VERIFY_DISPLAY_ERROR_CODE_LIST,
  VERIFY_SEND_EMAIL_OTP_DEFAULT_ERROR_CODE,
} from '../constants/constants';
import {
  getCustomerContactsApi,
  getSelectedPolicy,
} from '../selectors/changeContactSelector';
import {
  getForm,
  getNewEmail,
  getOtherPolicyList,
  getSelectedList,
} from '../selectors/changeEmailSelector';
import { getCustomerProfileCustKey } from '../selectors/customerProfileSelectors';
import {
  contactPermissionPolicy,
  submitChangeContactResponse,
} from '../types/changeContactService';
import {
  callBackErrorCodePage,
  returnErrorCode,
  sortPermissionPolicies,
} from '../utils';
import {
  fetchGoGreenPolicyList,
  fetchOtherPolicyList,
  resendOtpRequest,
  sendOtpRequest,
} from './changeContactSaga';

function* submitChangeEmailRequest(payload: any) {
  try {
    yield put({
      type: SUBMIT_CHANGE_EMAIL_REQUEST,
    });
    const { newEmail, selectedPolicyList } = payload;
    const res = {
      status: 200,
      json: () => require('../mock/submitChangeEmailResponse.json'),
    };
    // const url = `${customerProfileFeatureUrl}?device=WEB`;
    // let res = yield call(myAxaFetchWithToken, url, {
    //     method: 'GET',
    //     headers: {
    //         'Content-Type': 'application/json',
    //             body: JSON.stringify({newEmail, selectedPolicyList}),
    //     }
    // });
    const data = yield res.json();
    const response = data || [];

    yield put({
      type: SUBMIT_CHANGE_EMAIL_REQUEST_SUCCESS,
    });
    yield response;
  } catch (e) {
    yield put(showErrorModal());
  }
}

function* reviewChangeEmailForm(payload: any) {
  try {
    const selectedPolicy = yield select(getSelectedPolicy);
    const selectedList = yield select(getSelectedList);
    const otherPolicyList = yield select(getOtherPolicyList);
    const goGreenPolicyList = yield call(
      fetchGoGreenPolicyList,
      selectedList,
      otherPolicyList,
    );

    yield put(updateGoGreenList(goGreenPolicyList));
    if (goGreenPolicyList && goGreenPolicyList.length > 0) {
      goto(
        passPath('emma-customer-profile/changeemail/gogreen', '/renewalupdate'),
      );
    } else {
      goto(
        passPath('emma-customer-profile/changeemail/review', '/renewalupdate'),
      );
    }
  } catch (e) {
    yield put(showErrorModal());
  }
}

function* updateOtherPolices(payload: any) {
  try {
    const selectedPolicy = yield select(getSelectedPolicy);
    const otherPolicyList = yield call(fetchEmailOtherPolicyList);
    if (
      otherPolicyList.filter((policy: contactPermissionPolicy) => {
        if (policy.entityCode === ENTITY_CODE.EHK) {
          return (
            policy.policyNumber !== selectedPolicy.policyNumber ||
            policy.certificateNumber !==
              selectedPolicy.policy.certificateNumber ||
            policy.dependentNumber !== selectedPolicy.policy.dependentNumber
          );
        } else {
          return policy.policyNumber !== selectedPolicy.policyNumber;
        }
      }).length > 0
    ) {
      goto(
        passPath(
          'emma-customer-profile/changeemail/updatepolicy',
          '/renewalupdate',
        ),
      );
    } else {
      yield call(reviewChangeEmailForm, null);
    }
  } catch (e) {
    yield put(showErrorModal());
  }
}

function* submitChangeEmailForm(action: {
  type: string;
  payload: { otp: string; defaultErrorCode: string; featureId: string };
}) {
  const { otp, defaultErrorCode, featureId } = action.payload;
  try {
    const customerContactsApi = yield select(getCustomerContactsApi);
    const newEmail = yield select(getNewEmail);
    const selectedPolicy = yield select(getSelectedPolicy);
    const selectedList = yield select(getSelectedList);
    const otherPolicyList = yield select(getOtherPolicyList);
    const form = yield select(getForm);
    const selectedMainPolicy = otherPolicyList.find((policy: any) => {
      return policy.entityCode === ENTITY_CODE.EHK
        ? policy.policyNumber === selectedPolicy.policyNumber &&
            policy.certificateNumber ===
              selectedPolicy.policy.certificateNumber &&
            policy.dependentNumber === selectedPolicy.policy.dependentNumber
        : policy.policyNumber === selectedPolicy.policyNumber;
    });
    const selectedPolicyList = selectedList.map((policy: any) => ({
      policyNumber: policy.policyNumber,
      eCommFlag: policy.eCommEligible,
      entityCode: policy.entityCode,
      ...(policy.entityCode === ENTITY_CODE.EHK
        ? {
            certificateNumber: policy.certificateNumber,
            dependentNumber: policy.dependentNumber,
          }
        : {}),
    }));
    const { policyNumber, entityCode, policy } = selectedPolicy;
    const res = yield call(
      myAxaFetchWithToken,
      customerContactsApi,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          updateCustomerContactsRequest: {
            featureName: 'updateEmail',
            otp,
            platform: isApp() ? 'APP' : 'WEB',
            policy: {
              policyNumber,
              newEmail,
              eCommFlag: selectedMainPolicy.eCommEligible,
              ...(entityCode === ENTITY_CODE.EHK
                ? {
                    certificateNumber: policy.certificateNumber,
                    dependentNumber: policy.dependentNumber,
                  }
                : {}),
              entityCode,
            },
            additionalpolicies: selectedPolicyList,
            requestID: uuidv4(),
          },
        }),
      },
      undefined,
      undefined,
      0,
    );
    // const res = {
    //   status: 200,
    //   json: () => require('../mock/submitChangeEmailResponse.json'),
    // };
    const data: submitChangeContactResponse = yield res.json();

    const errorCode = _.get(data, 'response.reasonCode', '');
    handleErrorCode(errorCode, defaultErrorCode, featureId);
    if (_.isEmpty(errorCode) || errorCode === '200') {
      const response = data.response;
      const successfulPolicies = response.transactions
        .filter((transaction) => transaction.policyContactUpdated === 'Y')
        .map((transaction) =>
          otherPolicyList.find(
            (policy: contactPermissionPolicy) =>
              policy.policyNumber === transaction.policyNumber,
          ),
        )
        .filter((policy) => !!policy);
      const failedPolicies = response.transactions
        .filter((transaction) => transaction.policyContactUpdated === 'N')
        .map((transaction) =>
          otherPolicyList.find(
            (policy: contactPermissionPolicy) =>
              policy.policyNumber === transaction.policyNumber,
          ),
        )
        .filter((policy: contactPermissionPolicy) => !!policy);
      const isSubmitError =
        failedPolicies.length >= 1 && successfulPolicies.length >= 1;
      const cicCustKey = yield select(getCustomerProfileCustKey);
      // setup EmmaDM
      yield putCustState(
        'EmmaDM',
        JSON.stringify({
          DM2:
            // Fixed by HKEMMA-95687
            // Just in case, I keep both data type. It is now boolean.
            form.agreeNotify === 'true' || form.agreeNotify === true
              ? 'Y'
              : 'N',
        }),
        'emma',
      );
      // setup EmmaCIC
      yield put(
        updateEmailCustStateAction({
          custKey: cicCustKey,
          custValue: JSON.stringify({
            // Fixed by HKEMMA-95687
            // Just in case, I keep both data type. It is now boolean.
            DM2:
              form.agreeNotify === 'true' || form.agreeNotify === true
                ? 'Y'
                : 'N',
          }),
          channel: 'emma',
        }),
      );
      if (isSubmitError) {
        goto(
          passPath(
            'emma-customer-profile/changeemail/submiterror',
            '/renewalupdate',
          ),
        );
      } else {
        if (window && window.location.pathname.includes('/renewalupdate')) {
          EmmaFactory.createInstance().then((p) => {
            AppInsightsClient.getInstance().trackTrace(
              'GOTO emma-customer-profile/renew-success-callback?changeContactType=email',
            );
            p.instance.goTo(
              'emma-customer-profile/renew-success-callback?changeContactType=email',
            );
          });
        } else {
          yield put({
            type: SUBMIT_CHANGE_EMAIL_FORM_SUCCESS,
            transactionId: response.transactionId,
            transactions: response.transactions,
            successfulPolicies: sortPermissionPolicies(successfulPolicies),
            failedPolicies: sortPermissionPolicies(failedPolicies),
          });
          goto('emma-customer-profile/changeemail/confirm');
        }
      }
    } else {
      yield put({
        type: SUBMIT_CHANGE_EMAIL_FORM_FAIL,
      });
    }
  } catch (error) {
    if (error && error.json) {
      // could be empty response
      try {
        const data = yield error.json();
        const errorCode = _.get(data, 'response.reasonCode', defaultErrorCode);

        handleErrorCode(errorCode, defaultErrorCode, featureId);
      } catch (err) {
        console.log('Empty responses from: ', error);
        handleErrorCode(defaultErrorCode, defaultErrorCode, featureId);
      }
    } else {
      handleErrorCode(defaultErrorCode, defaultErrorCode, featureId);
    }
    yield put({
      type: SUBMIT_CHANGE_EMAIL_FORM_FAIL,
    });
  }
}

export function handleErrorCode(
  errorCode: string,
  defaultErrorCode: string,
  featureId: string,
) {
  if (_.isEmpty(errorCode) || errorCode === '200') {
    return;
  } else {
    const isDisplayError = VERIFY_DISPLAY_ERROR_CODE_LIST.includes(errorCode);
    if (isDisplayError) {
      store.dispatch({
        type: DISPLAY_ERROR_CODE,
        errorCode: errorCode,
      });
      store.dispatch({
        type: SUBMIT_CHANGE_EMAIL_FORM_FAIL,
        errorCode: errorCode,
      });
    } else {
      callBackErrorCodePage(
        featureId,
        returnErrorCode(errorCode, defaultErrorCode, featureId),
      );
    }
  }
}

function* updateEmailCustState(action: any) {
  try {
    const { custKey, custValue, channel } = action.payload;
    yield putCustState(
      `${custKey}-${moment().format('YYYYMMDD-HHmmss')}`,
      custValue,
      channel,
    );
    yield put({ type: UPDATE_EMAIL_CUST_STATE_SUCCESS });
  } catch (e) {
    yield put({ type: UPDATE_EMAIL_CUST_STATE_FAILURE });
  }
}

function* sendChangeEmailOtpRequest(action: any) {
  try {
    const newEmail = yield select(getNewEmail);
    const otpRes = yield call(sendOtpRequest, {
      featureName: 'updateEmail',
      email: newEmail,
    });
    const errorCode = _.get(otpRes, 'response.reasonCode', '');
    if (errorCode === '200') {
      yield put({
        type: SEND_EMAIL_OTP_REQUEST_SUCCESS,
      });

      goto(
        passPath('emma-customer-profile/changeemail/verify', '/renewalupdate'),
      );
    } else {
      callBackErrorCodePage(
        UPDATE_EMAIL_FEATURE_ID,
        errorCode || VERIFY_SEND_EMAIL_OTP_DEFAULT_ERROR_CODE,
      );
    }
  } catch (e) {
    yield put({
      type: SEND_EMAIL_OTP_REQUEST_FAIL,
    });
    // yield put(showErrorModal());
    callBackErrorCodePage(
      UPDATE_EMAIL_FEATURE_ID,
      VERIFY_SEND_EMAIL_OTP_DEFAULT_ERROR_CODE,
    );
  }
}

function* resendEmailOtpRequest(action: any) {
  try {
    const newEmail = yield select(getNewEmail);
    const otpRes = yield call(resendOtpRequest, {
      featureName: 'updateEmail',
      email: newEmail,
    });

    yield put({
      type: RESEND_EMAIL_OTP_SUCCESS,
    });
  } catch (e) {
    yield put({
      type: RESEND_EMAIL_OTP_FAIL,
    });
    yield put(showErrorModal());
  }
}

function* fetchEmailOtherPolicyList() {
  try {
    const newEmail = yield select(getNewEmail);
    const isRenewalUpdate =
      window && window.location.pathname.includes('/renewalupdate');

    const otherPolicyList = yield call(
      fetchOtherPolicyList,
      'updateEmail',
      {
        newEmail,
      },
      isRenewalUpdate,
    );
    yield put({ type: FETCH_EMAIL_OTHER_POLICY_LIST_SUCCESS, otherPolicyList });

    return otherPolicyList;
  } catch (e) {
    yield put({ type: FETCH_EMAIL_OTHER_POLICY_LIST_FAIL });
    yield put(showErrorModal());
  }
}

function* changeEmailSaga() {
  yield takeLatest(UPDATE_EMAIL_OTHER_POLICIES, updateOtherPolices);
  yield takeLatest(FETCH_EMAIL_OTHER_POLICY_LIST, fetchEmailOtherPolicyList);
  yield takeLatest(SUBMIT_CHANGE_EMAIL_FORM, submitChangeEmailForm);
  yield takeLatest(UPDATE_EMAIL_CUST_STATE, updateEmailCustState);
  yield takeLatest(REVIEW_CHANGE_EMAIL_FORM, reviewChangeEmailForm);
  yield takeLatest(SEND_EMAIL_OTP_REQUEST, sendChangeEmailOtpRequest);
  yield takeLatest(RESEND_EMAIL_OTP, resendEmailOtpRequest);
}

export default changeEmailSaga;
